import { useAPI } from '@providers/useAPI';
import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  GetJobSubscriptionsListResponseDto,
  GetNotificationSettingsResponseDto,
  GetWorkspaceSubscriptionsListResponseDto,
  JobSubscriptionsService,
  NotificationsService,
  SubscriptionsListResponseDto,
  UpdateNotificationSettingsBodyDto,
  WorkspaceSubscriptionsService,
} from 'app/API';
import { createFetchJobSubscriptionsQueryFn } from 'app/queries/notifications/createFetchJobSubscriptionsQueryFn';
import { createFetchNotificationSettingsQueryFn } from 'app/queries/notifications/createFetchNotificationSettingsQueryFn';
import { createFetchPushSubscriptionsQueryFn } from 'app/queries/notifications/createFetchPushSubscriptionsQueryFn';
import { createFetchWorkspaceSubscriptionsQueryFn } from 'app/queries/notifications/createFetchWorkspaceSubscriptionsQueryFn';
import { notificationsKeys } from 'app/queries/notifications/notificationsKeys';
import { createContext, useContext, ReactNode } from 'react';
import toast from 'react-hot-toast';

type TState = {
  settings: GetNotificationSettingsResponseDto | undefined;
  subscriptions: SubscriptionsListResponseDto[];
  jobSubscriptions: GetJobSubscriptionsListResponseDto[];
  workspaceSubscriptions: GetWorkspaceSubscriptionsListResponseDto[];
  updateSettings: (v: UpdateNotificationSettingsBodyDto) => void;
  deleteSubscription: (v: string) => void;
  unwatchJob: (v: string) => void;
  unwatchWorkspace: (v: string) => void;
  retryFailedQueries: () => void;
  isBusy: boolean;
  isUpdatingSettings: boolean;
  isRemovingSubscription: boolean;
  hasLoadError: boolean;
};

const PageContext = createContext<TState>({
  settings: undefined,
  subscriptions: [],
  jobSubscriptions: [],
  workspaceSubscriptions: [],
  updateSettings: () => {},
  deleteSubscription: () => {},
  unwatchJob: () => {},
  unwatchWorkspace: () => {},
  retryFailedQueries: () => {},
  isBusy: false,
  isUpdatingSettings: false,
  isRemovingSubscription: false,
  hasLoadError: false,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: notificationsKeys.settings(),
    queryFn: createFetchNotificationSettingsQueryFn(call),
  });
  const subscriptionsQuery = useQuery({
    queryKey: notificationsKeys.pushSubscriptions(),
    queryFn: createFetchPushSubscriptionsQueryFn(call),
  });
  const jobSubscriptionsQuery = useQuery({
    queryKey: notificationsKeys.jobSubscriptionsList(),
    queryFn: createFetchJobSubscriptionsQueryFn(call),
  });
  const workspaceSubscriptionsQuery = useQuery({
    queryKey: notificationsKeys.workspaceSubscriptionsList(),
    queryFn: createFetchWorkspaceSubscriptionsQueryFn(call),
  });
  const queries = [settingsQuery, subscriptionsQuery, jobSubscriptionsQuery, workspaceSubscriptionsQuery];

  const invalidate = (queryKey: QueryKey) => queryClient.invalidateQueries({ queryKey });

  const updateSettingsMutation = useMutation({
    mutationFn: (requestBody: UpdateNotificationSettingsBodyDto) =>
      call(() => NotificationsService.updateNotificationSettings({ requestBody })),
    onSuccess: async (data) => {
      await queryClient.cancelQueries({ queryKey: notificationsKeys.settings(), exact: true });
      queryClient.setQueryData(notificationsKeys.settings(), data);
    },
    onError: () => {
      toast.error('Failed to update the notification settings');
      return invalidate(notificationsKeys.settings());
    },
  });

  const deleteSubscriptionMutation = useMutation({
    mutationFn: (sid: string) => call(() => NotificationsService.deleteSubscription({ sid })),
    onError: () => toast.error('Failed to remove the device'),
    onSettled: () => invalidate(notificationsKeys.pushSubscriptions()),
  });

  const unwatchJobMutation = useMutation({
    mutationFn: (jobId: string) => call(() => JobSubscriptionsService.delete({ jobId })),
    onError: () => toast.error('Failed to remove the job subscription'),
    onSettled: () => invalidate(notificationsKeys.jobSubscriptions()),
  });

  const unwatchWorkspaceMutation = useMutation({
    mutationFn: (workspaceId: string) => call(() => WorkspaceSubscriptionsService.delete({ workspaceId })),
    onError: () => toast.error('Failed to remove the workspace subscription'),
    onSettled: () => invalidate(notificationsKeys.workspaceSubscriptions()),
  });

  const isRemovingSubscription =
    deleteSubscriptionMutation.isPending || unwatchJobMutation.isPending || unwatchWorkspaceMutation.isPending;

  const retryFailedQueries = () => {
    queries.filter((query) => query.isError).forEach((query) => void query.refetch());
  };

  return (
    <PageContext.Provider
      value={{
        settings: settingsQuery.data,
        subscriptions: subscriptionsQuery.data ?? [],
        jobSubscriptions: jobSubscriptionsQuery.data ?? [],
        workspaceSubscriptions: workspaceSubscriptionsQuery.data ?? [],
        updateSettings: (settings) => updateSettingsMutation.mutate(settings),
        deleteSubscription: (sid) => deleteSubscriptionMutation.mutate(sid),
        unwatchJob: (jobId) => unwatchJobMutation.mutate(jobId),
        unwatchWorkspace: (workspaceId) => unwatchWorkspaceMutation.mutate(workspaceId),
        retryFailedQueries,
        isBusy: queries.some((query) => query.isFetching) || updateSettingsMutation.isPending || isRemovingSubscription,
        isUpdatingSettings: updateSettingsMutation.isPending,
        isRemovingSubscription,
        hasLoadError: queries.some((query) => query.isError),
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
